import React from 'react';
import * as Sentry from '@sentry/react';
import {useParams} from 'react-router-dom';
import {Trans, useTranslation} from 'react-i18next';
import {CircleQuestionMarkIcon} from 'lucide-react';
import {useSummary} from '../../../../context/summary';
import {useDocumentSelfieDetection} from 'hooks';
import {ButtonProps} from 'components/common/Button';
import {IV_DOCUMENT_IMAGE_TYPES, IV_PHOTO_SOURCE} from '@chekin/core';
import {
  Button,
  cn,
  DEVICE,
  useModalControls,
  useScreenResize,
} from '@chekin/guestapp-ui-kit';
import {useLayoutMode} from 'hooks/useLayoutMode';
import passportGif from 'assets/img/ocr-passport-info.gif?no-inline';
import frontSidePlaceholder from '../../../../assets/img/frontside-placeholder.svg?no-inline';
import MRZPageLayout from '../../MRZPageLayout';
import ModalSheet from 'components/common/ModalSheet';
import StandardModalSheet from 'components/common/StandardModalSheet';
import {PhotoBox} from '@chekin/shared';
import {HelperText, ModalContent} from './styled';

function MRZScanView() {
  const {t} = useTranslation();
  const {uploadType} = useParams();
  const {isMatch: isMobileMode} = useScreenResize(DEVICE.mobileXL);
  const layoutMode = useLayoutMode();
  const docType = IV_DOCUMENT_IMAGE_TYPES.back;

  const isUpload = uploadType === 'upload';
  const mobilePageTitle = isUpload && isMobileMode ? 'guest_data' : 'scan_your_document';
  const {
    isOpen: isOpenNeedHelpModal,
    openModal: openNeedHelpModal,
    closeModal: closeNeedHelpModal,
  } = useModalControls();

  const {ProcessModal, ErrorModal, RetryModal, sendAndReadImageForOCR} =
    useDocumentSelfieDetection({docType, onlyScanning: true});

  const {reservationId, countryCode, data: summary} = useSummary();

  const modalSheetButtons: ButtonProps[] = React.useMemo(() => {
    const primaryButton = {
      label: t('understood'),
      primary: true,
      onClick: closeNeedHelpModal,
    };
    return isMobileMode ? [] : [primaryButton];
  }, [t, closeNeedHelpModal, isMobileMode]);

  const handleChangePhoto = React.useCallback(
    ({image, source}: {image: string; source: IV_PHOTO_SOURCE}) => {
      if (countryCode) {
        sendAndReadImageForOCR({image, source, countryCode, reservationId});
      } else {
        console.error('countryCode is missing', summary);
        Sentry.captureMessage('countryCode is missing', {extra: {summary}});
      }
    },
    [countryCode, reservationId, sendAndReadImageForOCR, summary],
  );

  const modalContent = (
    <ModalContent>
      <div>
        <Trans i18nKey="tool_tip_mrz_text_one">
          Make sure the photo of your document has enough quality to detect the{' '}
          <b>MRZ code</b> of the document.
        </Trans>
      </div>
      <div>{t('tool_tip_mrz_text_two')}</div>
    </ModalContent>
  );

  return (
    <MRZPageLayout title={t(mobilePageTitle)}>
      <HelperText className="mrz-scan-view__helper">
        {t('upload_mrz_helper_text')}
      </HelperText>
      <Button
        variant="secondary"
        onClick={openNeedHelpModal}
        className={cn(
          'mrz-scan-view__question-btn',
          'mb-[30px] h-auto w-auto min-w-0 self-center gap-2 text-sm no-underline',
        )}
      >
        <CircleQuestionMarkIcon size={16} />
        {t('need_help_question')}
      </Button>
      <PhotoBox
        mobileHelperTitle={t('scan_your_document')}
        mobileHelperText={t('code_mrz_helper_text')}
        onUploadPhoto={handleChangePhoto}
        preloadedScreenshot={''}
        isUpload={isUpload}
        placeholderImage={frontSidePlaceholder}
        layoutMode={layoutMode}
      />
      <ProcessModal />
      <ErrorModal />
      <RetryModal />

      <ModalSheet
        isOpenModalSheet={isOpenNeedHelpModal}
        handleCloseModal={closeNeedHelpModal}
      >
        <StandardModalSheet
          buttons={modalSheetButtons}
          text={modalContent}
          img={passportGif}
        />
      </ModalSheet>
    </MRZPageLayout>
  );
}

export {MRZScanView};
